/**
 * WEBSITE-ONLY pending checkout store.
 *
 * Persists the public website booking journey (quote → courier → booking form)
 * across authentication redirects, page refreshes and mobile browser handoffs.
 *
 * NOT used by Malak (AI chat), WhatsApp or Messenger flows. Malak keeps its own
 * server-side session state (`malak_booking_sessions`) and never reads this key.
 */

const STORAGE_KEY = "ec_pending_checkout_v1";
export const PENDING_CHECKOUT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

export type CheckoutStep = "courier_selected" | "booking_details" | "payment";

export interface PendingCheckout {
  version: 1;
  savedAt: number;
  /** Anonymous website session identifier (not a Malak session). */
  anonSessionId: string;
  step: CheckoutStep;
  quoteId: string | null;
  courierTripId: string | null;
  quoteData: Record<string, unknown> | null;
  selectedCourier: Record<string, unknown> | null;
  /** Shipment/contact info already collected on the booking form. */
  form: Record<string, unknown> | null;
  /** Customer explicitly chose "continue as guest". */
  guest: boolean;
  returnTo: string | null;
}

function newAnonSessionId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch { /* ignore */ }
  return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function read(): PendingCheckout | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCheckout;
    if (!parsed || parsed.version !== 1 || typeof parsed.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt > PENDING_CHECKOUT_TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function write(value: PendingCheckout): PendingCheckout {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    }
  } catch { /* quota / private mode — non-fatal */ }
  return value;
}

/** Read the live pending checkout, or null when absent/expired. */
export function loadPendingCheckout(): PendingCheckout | null {
  return read();
}

/** Create or merge into the pending checkout. Refreshes the TTL. */
export function savePendingCheckout(patch: Partial<Omit<PendingCheckout, "version" | "savedAt">>): PendingCheckout {
  const existing = read();
  const next: PendingCheckout = {
    version: 1,
    savedAt: Date.now(),
    anonSessionId: existing?.anonSessionId ?? newAnonSessionId(),
    step: patch.step ?? existing?.step ?? "courier_selected",
    quoteId: patch.quoteId !== undefined ? patch.quoteId : existing?.quoteId ?? null,
    courierTripId:
      patch.courierTripId !== undefined ? patch.courierTripId : existing?.courierTripId ?? null,
    quoteData: patch.quoteData !== undefined ? patch.quoteData : existing?.quoteData ?? null,
    selectedCourier:
      patch.selectedCourier !== undefined ? patch.selectedCourier : existing?.selectedCourier ?? null,
    form: patch.form !== undefined ? patch.form : existing?.form ?? null,
    guest: patch.guest !== undefined ? patch.guest : existing?.guest ?? false,
    returnTo: patch.returnTo !== undefined ? patch.returnTo : existing?.returnTo ?? null,
  };
  return write(next);
}

export function clearPendingCheckout(): void {
  try {
    if (typeof window !== "undefined") window.localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

export function hasPendingCheckout(): boolean {
  return read() !== null;
}
